// Web Push helper — VAPID-based push subscription flow.
// Registers /sw.js, requests permission, subscribes to PushManager, and
// persists the subscription on the backend (bound to the player's email
// so streak-danger reminders can be personalized).
import axios from "axios";
import { getEmail } from "@/lib/pro";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUB_KEY_LS = "ppe_push_subscription_v1";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getRegistration() {
  const reg =
    (await navigator.serviceWorker.getRegistration("/")) ||
    (await navigator.serviceWorker.register("/sw.js", { scope: "/" }));
  await navigator.serviceWorker.ready;
  return reg;
}

export async function currentPushSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function enablePush() {
  if (!pushSupported()) throw new Error("unsupported");
  // Only ask if not already denied.
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") return { status: perm };

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await axios.get(`${API}/push/vapid-public-key`);
    if (!data.publicKey) throw new Error("no-vapid-key");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  const raw = sub.toJSON();
  const email = getEmail();
  await axios.post(`${API}/push/subscribe`, {
    endpoint: raw.endpoint,
    expirationTime: raw.expirationTime,
    keys: raw.keys,
    email: email || null,
  });
  try {
    localStorage.setItem(SUB_KEY_LS, raw.endpoint);
  } catch {}
  return { status: "granted", subscription: sub };
}

export async function disablePush() {
  if (!pushSupported()) return { status: "unsupported" };
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return { status: "not-subscribed" };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { status: "not-subscribed" };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {}
  try {
    await axios.post(`${API}/push/unsubscribe`, { endpoint });
  } catch {}
  try {
    localStorage.removeItem(SUB_KEY_LS);
  } catch {}
  return { status: "unsubscribed" };
}

export async function pushSubscribed() {
  const sub = await currentPushSubscription();
  return !!sub && Notification.permission === "granted";
}
